import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { 
  useGetVapidPublicKeyQuery,
  useSubscribeToPushMutation, 
  useUnsubscribeFromPushMutation,
  useGetUserSubscriptionsQuery 
} from '../newapi/pwa/pwaSlice';

const usePushNotifications = () => {
  const [isSupported, setIsSupported] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [permission, setPermission] = useState('default');
  const [isLoading, setIsLoading] = useState(false);
  
  // Get current user from Redux store (adjust path as needed)
  const currentUser = useSelector((state) => state.auth?.user || state.user?.currentUser);
  const userId = currentUser?.id;

  // RTK Query hooks
  const { data: vapidKey, isLoading: vapidLoading } = useGetVapidPublicKeyQuery();
  const { data: userSubscriptions } = useGetUserSubscriptionsQuery();
  const [subscribeToPushMutation,{isLoading:subscribeLoading}] = useSubscribeToPushMutation();
  const [unsubscribeFromPushMutation,{isLoading:unsubscribeLoading}] = useUnsubscribeFromPushMutation();

  useEffect(() => {
    // Check if push notifications are supported
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      setIsSupported(true);
      setPermission(Notification.permission);
    }
  }, []);

  // Convert VAPID key to Uint8Array
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  // Subscribe user to push notifications
  const subscribeToPush = async (deviceName = "") => {
    if(deviceName.length === 0) {
      deviceName = null;
    }
    if (!isSupported || !userId || !vapidKey) {
      console.log('Push notifications not supported, user not logged in, or VAPID key not available');
      return null;
    }

    setIsLoading(true);
    try {
      // Request permission
      const permission = await Notification.requestPermission();
      setPermission(permission);

      if (permission !== 'granted') {
        console.log('Permission not granted for push notifications');
        setIsLoading(false);
        return null;
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;


      // Subscribe to push manager
      const pushSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });

      setSubscription(pushSubscription);

      // Send subscription to backend using RTK Query
      const data = await subscribeToPushMutation({
        userId,
        subscription: pushSubscription,
        userAgent: navigator.userAgent,
        deviceInfo: {
          timestamp: new Date().toISOString(),
          deviceName:deviceName,
        }
      }).unwrap();


      setIsLoading(false);
      return pushSubscription;

    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
      setIsLoading(false);
      return null;
    }
  };

  // Unsubscribe from push notifications
  const unsubscribeFromPush = async () => {
    if (!subscription) return;

    setIsLoading(true);
    try {
      await subscription.unsubscribe();
      // console.log(subscription);
      
      // Remove subscription from backend using RTK Query
      await unsubscribeFromPushMutation({
        userId,
        endpoint: subscription.endpoint
      }).unwrap();
      
      setSubscription(null);
      // console.log('Successfully unsubscribed from push notifications');
      setIsLoading(false);
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
      setIsLoading(false);
    }
  };

  // Check existing subscription
  const checkExistingSubscription = async () => {
    if (!isSupported) return;

    try {
      const registration = await navigator.serviceWorker.ready;
      const existingSubscription = await registration.pushManager.getSubscription();
      if (existingSubscription) {
        setSubscription(existingSubscription);
        // console.log('Existing subscription found:', existingSubscription);
      }
    } catch (error) {
      // console.error('Error checking existing subscription:', error);
    }
  };

  // Auto-subscribe when user logs in
  useEffect(() => {
    if (userId && isSupported && permission === 'granted' && !subscription) {
      checkExistingSubscription();
    }
  }, [userId, isSupported, permission]);

  return {
    userSubscriptions,
    isSupported,
    subscription,
    permission,
    isLoading,
    subscribeToPush,
    unsubscribeFromPush,
    checkExistingSubscription,
    subscribeLoading,
    unsubscribeLoading
  };
};

export default usePushNotifications; 